import { NavLink } from 'react-router-dom'
import { PREFETCH } from '@/routes/prefetch'
import { cn } from '@/lib/cn'
import { IconButton } from '@/components/ui/IconButton'
import {
  ICON_SIZE,
  ICON_STROKE,
  LayoutDashboard,
  MessagesSquare,
  MessageCircle,
  MessageSquare,
  Mail,
  Mic,
  Users,
  Settings,
  ScrollText,
  PanelLeftClose,
  PanelLeftOpen,
  ExternalLink,
  type LucideIcon,
} from '@/components/icons'

/** The left menu bar. */

export interface NavEntry {
  label: string
  to: string
  icon: LucideIcon
  /** Any one of these grants the item. Absent = always visible. */
  anyOf?: string[]
  badge?: number
}

export interface NavGroup {
  heading?: string
  items: NavEntry[]
}

export const NAV: NavGroup[] = [
  { items: [{ label: 'Overview', to: '/', icon: LayoutDashboard, anyOf: ['overview.view'] }] },
  {
    heading: 'Communications',
    items: [{ label: 'Chatbot', to: '/chatbot', icon: MessagesSquare, anyOf: ['leads.view', 'chats.view'] }],
  },
  {
    heading: 'Administration',
    items: [
      { label: 'Users', to: '/users', icon: Users, anyOf: ['users.view'] },
      { label: 'Settings', to: '/settings', icon: Settings, anyOf: ['settings.view'] },
      { label: 'Audit log', to: '/audit', icon: ScrollText, anyOf: ['audit.view'] },
    ],
  },
]

export function Sidebar({
  permissions,
  collapsed,
  onToggleCollapse,
  urgentCount,
  className,
}: {
  permissions: ReadonlySet<string>
  collapsed: boolean
  onToggleCollapse: () => void
  urgentCount?: number
  className?: string
}) {
  const visibleGroups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((i) => !i.anyOf || i.anyOf.some((p) => permissions.has(p))),
  })).filter((group) => group.items.length > 0) // a group with no children hides its heading.
  
  const hasVoiceAgentPerm = permissions.has('voice.view');

  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex h-full flex-col border-r border-line bg-surface',
        collapsed ? 'w-16' : 'w-60',
        'transition-[width] duration-150',
        className,
      )}
    >
      <div className={cn('flex h-14 shrink-0 items-center border-b border-line',
        collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        {!collapsed && (
          <span className="flex flex-col leading-tight">
            <span className="text-h3 font-semibold text-text">Paisa24</span>
            <span className="text-xs text-subtle">Control Panel</span>
          </span>
        )}
        <IconButton
          icon={collapsed ? PanelLeftOpen : PanelLeftClose}
          label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          size="sm"
          onClick={onToggleCollapse}
        />
      </div>

      <div className="flex-1 overflow-y-auto py-2">
        {visibleGroups.map((group, gi) => (
          <div key={group.heading ?? gi} className="mb-1">
            {group.heading && !collapsed && (
              <p className="px-4 pt-3 pb-1 text-xs font-semibold tracking-wide text-subtle uppercase">
                {group.heading}
              </p>
            )}
            <ul className="flex flex-col gap-0.5 px-2">
              {group.items.map((item) => (
                <li key={item.to}>
                  <NavItem
                    item={item}
                    collapsed={collapsed}
                    badge={item.to === '/chatbot' ? urgentCount : undefined}
                  />
                </li>
              ))}

              {group.heading === 'Communications' && !collapsed && (
                <>
                  <li>
                    <div
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-base text-subtle"
                      title="WhatsApp automation is in development"
                    >
                      <MessageCircle size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
                      <span className="flex-1">WhatsApp</span>
                      <span className="rounded-sm border border-line bg-neutral-bg px-1.5 py-0.5 text-xs">
                        Coming
                      </span>
                    </div>
                  </li>
                  {hasVoiceAgentPerm && (
                    <li>
                      <NavLink
                        to="/voice"
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium transition-colors ${
                            isActive
                              ? 'bg-hover-bg text-primary'
                              : 'text-subtle hover:bg-hover-bg hover:text-default'
                          }`
                        }
                      >
                        <Mic size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
                        <span className="flex-1">Voice Agent</span>
                        <span className="rounded-sm border border-line bg-neutral-bg px-1.5 py-0.5 text-xs">
                          In Dev
                        </span>
                      </NavLink>
                    </li>
                  )}
                  <li>
                    <div
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-base text-subtle"
                      title="Email is on hold"
                    >
                      <Mail size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
                      <span className="flex-1">Email</span>
                      <span className="rounded-sm border border-line bg-neutral-bg px-1.5 py-0.5 text-xs">
                        On hold
                      </span>
                    </div>
                  </li>
                  <li>
                    <div
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-base text-subtle"
                      title="Text (SMS) is on hold"
                    >
                      <MessageSquare size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
                      <span className="flex-1">Text (SMS)</span>
                      <span className="rounded-sm border border-line bg-neutral-bg px-1.5 py-0.5 text-xs">
                        On hold
                      </span>
                    </div>
                  </li>
                </>
              )}
            </ul>
          </div>
        ))}
      </div>

      <div className="shrink-0 border-t border-line p-2">
        <a
          href="https://paisa24finance.com/"
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? 'Visit paisa24finance.com' : undefined}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-base text-muted transition-colors duration-150',
            'hover:bg-hover hover:text-text',
            'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
            collapsed && 'justify-center px-0',
          )}
        >
          <ExternalLink size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
          {!collapsed && <span className="flex-1">paisa24finance.com</span>}
        </a>
      </div>
    </nav>
  )
}

function NavItem({
  item,
  collapsed,
  badge,
}: {
  item: NavEntry
  collapsed: boolean
  badge?: number
}) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      // Warm the screen's chunk before the click lands. Cheap, idempotent, and
      // it turns a cold navigation into an instant one.
      onMouseEnter={() => { void PREFETCH[item.to]?.() }}
      onFocus={() => { void PREFETCH[item.to]?.() }}
      // `end` on the index route only, or "/" stays active on every page.
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-base transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-primary-subtle font-medium text-primary-active'
            : 'text-muted hover:bg-hover hover:text-text',
        )
      }
    >
      <Icon size={ICON_SIZE.md} strokeWidth={ICON_STROKE} aria-hidden />
      {!collapsed && <span className="flex-1">{item.label}</span>}
      {!collapsed && badge !== undefined && badge > 0 && (
        <span className="rounded-full bg-urgent-bg px-1.5 py-0.5 text-xs font-medium text-urgent tabular">
          {badge}
        </span>
      )}
    </NavLink>
  )
}
